(() => {
  'use strict';

  const body = document.body;
  if (!body || body.dataset.scannerSiteMode !== 'true') return;

  const previewBarSelectors = [
    '#OnlineStorePreviewBarNextData',
    '#preview-bar-iframe',
    'iframe[src*="preview_bar"]',
    '#PBarNextFrame',
    '#PBarNextFrameWrapper',
    '[data-shopify-preview-bar]',
  ];

  const hasPreviewBar = () => previewBarSelectors.some((selector) => document.querySelector(selector));
  const hasUnpublishedThemeRole = () => {
    const role = String((window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '')
      .trim()
      .toLowerCase();
    return role !== '' && role !== 'main';
  };

  const syncPreviewMode = () => {
    const previewMode = Boolean(window.Shopify && window.Shopify.designMode)
      || /(?:^|[?&])preview_theme_id=/.test(window.location.search)
      || /(?:^|\.)shopifypreview\.com$/i.test(window.location.hostname)
      || window.self !== window.top
      || hasUnpublishedThemeRole()
      || hasPreviewBar();

    if (previewMode) body.classList.add('uth-shopify-preview');
    return previewMode;
  };

  syncPreviewMode();
  window.addEventListener('load', syncPreviewMode, { once: true });

  if ('MutationObserver' in window && !body.classList.contains('uth-shopify-preview')) {
    let frame = 0;
    const observer = new MutationObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (syncPreviewMode()) observer.disconnect();
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }

  const dispatchNia = (detail = {}) => {
    const openNia = () => {
      const root = document.querySelector('[data-uth-nia-root]');
      if (root && root.dataset.externalModalActive === 'true') return;

      const launcher = document.querySelector('[data-uth-nia-launcher]');
      if (launcher && launcher.getAttribute('aria-expanded') !== 'true') {
        launcher.click();
      }

      window.setTimeout(() => {
        document.dispatchEvent(new CustomEvent('uth:nia:commerce', { detail }));
      }, 80);
    };

    if (typeof window.UTHNiaLoad === 'function') {
      window.UTHNiaLoad().then((ready) => {
        if (ready !== false) openNia();
      });
      return;
    }

    openNia();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-uth-nia-open]');
    if (opener) {
      event.preventDefault();
      dispatchNia({ action: 'open' });
      return;
    }

    const fit = event.target.closest('[data-uth-nia-fit]');
    if (fit) {
      event.preventDefault();
      dispatchNia({ action: 'fit-check' });
      return;
    }

    const prompt = event.target.closest('[data-uth-nia-prompt]');
    if (prompt) {
      event.preventDefault();
      dispatchNia({ action: 'prompt', prompt: prompt.dataset.uthNiaPrompt || '' });
    }
  });

  const gallery = document.querySelector('[data-uth-commerce-gallery]');
  if (gallery) {
    const stage = gallery.querySelector('.uth-commerce-gallery__stage');
    const image = gallery.querySelector('[data-uth-commerce-main-image]');
    const label = gallery.querySelector('[data-uth-commerce-media-label]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-uth-commerce-thumb]'));

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (!image || !thumb.dataset.imageSrc) return;

        thumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        const next = new Image();
        next.onload = () => {
          if (stage) stage.classList.add('is-switching');
          window.setTimeout(() => {
            if (thumb.dataset.imageSrcset) image.srcset = thumb.dataset.imageSrcset;
            else image.removeAttribute('srcset');
            if (thumb.dataset.imageSizes) image.sizes = thumb.dataset.imageSizes;
            else image.removeAttribute('sizes');
            image.src = thumb.dataset.imageSrc;
            image.alt = thumb.dataset.imageAlt || '';
            if (label) label.textContent = thumb.dataset.imageLabel || 'Product image';
            if (stage) stage.classList.remove('is-switching');
          }, 90);
        };
        next.src = thumb.dataset.imageSrc;
      });
    });
  }

  const productRoot = document.querySelector('[data-uth-commerce-product]');
  if (productRoot) {
    const variantSelect = productRoot.querySelector('[data-uth-commerce-variant-select]');
    const priceTargets = productRoot.querySelectorAll('[data-uth-commerce-price], [data-uth-commerce-button-price], [data-uth-commerce-sticky-price]');
    const addButtons = productRoot.querySelectorAll('[data-uth-commerce-add], [data-uth-commerce-sticky-add]');

    const syncVariant = () => {
      if (!variantSelect) return;
      const option = variantSelect.options[variantSelect.selectedIndex];
      if (!option) return;

      const price = option.dataset.price || '';
      const available = option.dataset.available === 'true';
      const priceReady = option.dataset.priceReady === 'true';
      const purchasable = available && priceReady;

      priceTargets.forEach((target) => {
        if (price) target.textContent = price;
      });

      addButtons.forEach((button) => {
        button.disabled = !purchasable;
        button.setAttribute('aria-disabled', purchasable ? 'false' : 'true');
        if (button.matches('[data-uth-commerce-add]')) {
          button.firstChild.textContent = purchasable ? 'Buy now · ' : (available ? 'Price unavailable · ' : 'Sold out · ');
        } else {
          button.textContent = purchasable ? 'Buy now' : (available ? 'Price unavailable' : 'Sold out');
        }
      });
    };

    if (variantSelect) {
      variantSelect.addEventListener('change', syncVariant);
      syncVariant();
    }

  }


  // Buy Now means checkout with the selected scanner quantity, not add another
  // scanner on every revisit. Never clear the cart or touch unrelated lines.
  const planScannerPurchase = (cart, variantId, quantity, quantityEdited, isHeader) => {
    const matching = (cart.items || []).filter((item) => String(item.variant_id || item.id) === String(variantId));
    if (!matching.length) return { action: 'add', quantity };
    if (isHeader || !quantityEdited) return { action: 'checkout' };
    const currentQuantity = matching.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (currentQuantity === quantity) return { action: 'checkout' };
    // Multiple lines may have distinct properties or discounts; leave them intact.
    if (matching.length !== 1) return { action: 'review' };
    return { action: 'change', key: matching[0].key, quantity };
  };

  if (!window.__uthScannerBuyNowInstalled && typeof window.fetch === 'function') {
    window.__uthScannerBuyNowInstalled = true;
    let purchaseInFlight = false;
    let buttonStates = [];
    let activeForm = null;
    let statusBox = null;
    const editedQuantities = new WeakSet();
    const purchaseButtons = '[data-uth-commerce-add], [data-uth-commerce-sticky-add], button.uth-scanner-header__buy';
    const routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const route = (path) => routeRoot.replace(/\/?$/, '/') + path;

    const resetPurchase = () => {
      purchaseInFlight = false;
      buttonStates.forEach(({ button, disabled, ariaDisabled }) => {
        button.disabled = disabled;
        if (ariaDisabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', ariaDisabled);
      });
      buttonStates = [];
      if (activeForm) activeForm.removeAttribute('aria-busy');
      activeForm = null;
    };
    const showStatus = (message, error = false) => {
      if (!statusBox) {
        statusBox = document.createElement('div');
        statusBox.setAttribute('data-uth-checkout-status', '');
        Object.assign(statusBox.style, {
          position: 'fixed', top: '110px', left: '50%', transform: 'translateX(-50%)',
          width: 'min(440px, calc(100vw - 32px))', padding: '16px 20px', borderRadius: '12px',
          border: '1px solid #69717c', background: '#141518', color: '#f5f6f8',
          boxShadow: '0 8px 32px #0009', zIndex: '10000', fontSize: '14px', lineHeight: '1.5'
        });
        document.body.appendChild(statusBox);
      }
      statusBox.setAttribute('role', error ? 'alert' : 'status');
      statusBox.replaceChildren(document.createTextNode(message));
      if (error) {
        const link = document.createElement('a');
        link.href = route('cart');
        link.textContent = ' Review your cart';
        link.style.color = '#f5f6f8';
        link.style.textDecoration = 'underline';
        statusBox.appendChild(link);
        const close = document.createElement('button');
        close.type = 'button';
        close.textContent = 'Dismiss';
        close.setAttribute('aria-label', 'Dismiss checkout message');
        Object.assign(close.style, { display: 'block', marginTop: '12px', padding: '4px 0', background: 'none', border: '0', color: '#f5f6f8', cursor: 'pointer', textDecoration: 'underline' });
        close.addEventListener('click', () => { statusBox.remove(); statusBox = null; });
        statusBox.appendChild(close);
      }
    };
    const readJson = async (response) => {
      const result = await response.json();
      if (!response.ok) throw new Error('Cart request was not confirmed.');
      return result;
    };
    const markQuantityEdited = (event) => {
      if (event.target.matches && event.target.matches('[data-uth-commerce-quantity]')) editedQuantities.add(event.target);
    };
    document.addEventListener('input', markQuantityEdited, true);
    document.addEventListener('change', markQuantityEdited, true);
    window.addEventListener('pageshow', () => {
      resetPurchase();
      if (statusBox) { statusBox.remove(); statusBox = null; }
    });

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches || !form.matches('.uth-commerce-product-form, .uth-scanner-header__buy-form')) return;
      const submitter = event.submitter;
      // Accelerated payment buttons remain owned by Shopify.
      if (submitter && !submitter.matches(purchaseButtons)) return;
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (purchaseInFlight) { event.stopImmediatePropagation(); return; }
      if (submitter && (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true')) return;
      if (!form.reportValidity()) return;

      const variantInput = form.elements.namedItem('id');
      const quantityInput = form.elements.namedItem('quantity');
      const variantId = variantInput && String(variantInput.value);
      const quantity = Number(quantityInput ? quantityInput.value : 1);
      if (!variantId || !Number.isInteger(quantity) || quantity < 1) {
        showStatus('Choose a valid scanner quantity before continuing.', true);
        return;
      }
      const isHeader = form.matches('.uth-scanner-header__buy-form');
      const quantityEdited = !isHeader && quantityInput && (editedQuantities.has(quantityInput) || quantityInput.value !== quantityInput.defaultValue);
      purchaseInFlight = true;
      activeForm = form;
      form.setAttribute('aria-busy', 'true');
      buttonStates = Array.from(document.querySelectorAll(purchaseButtons)).map((button) => ({ button, disabled: button.disabled, ariaDisabled: button.getAttribute('aria-disabled') }));
      buttonStates.forEach(({ button }) => { button.disabled = true; button.setAttribute('aria-disabled', 'true'); });
      showStatus('Checking your cart and opening checkout…');
      try {
        const cart = await window.fetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(readJson);
        const plan = planScannerPurchase(cart, variantId, quantity, quantityEdited, isHeader);
        if (plan.action === 'review') {
          resetPurchase();
          showStatus('Your cart has scanner lines with different options. Change their quantities in your cart before checkout.', true);
          return;
        }
        if (plan.action !== 'checkout') {
          const payload = plan.action === 'change'
            ? { id: plan.key, quantity: plan.quantity }
            : { items: [{ id: variantId, quantity: plan.quantity }] };
          await window.fetch(route(plan.action === 'change' ? 'cart/change.js' : 'cart/add.js'), {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
          }).then(readJson);
        }
        // Keep the lock until navigation completes, including slow networks.
        window.location.assign(route('checkout'));
      } catch (_) {
        resetPurchase();
        // Never automatically retry an uncertain cart mutation.
        showStatus('We couldn’t confirm the cart update. Check your cart before trying again.', true);
      }
    }, true);
  }

  const stickyBar = document.querySelector('[data-uth-commerce-mobile-bar]');
  if (stickyBar && 'IntersectionObserver' in window) {
    const footer = document.querySelector('.uth-footer--scanner');
    if (footer) {
      const observer = new IntersectionObserver((entries) => {
        const footerVisible = entries.some((entry) => entry.isIntersecting);
        stickyBar.toggleAttribute('hidden', footerVisible);
      }, { rootMargin: '0px 0px 24px 0px', threshold: 0 });
      observer.observe(footer);
    }
  }
})();
